"use client";

import { ClockIcon } from "@heroicons/react/20/solid";
import { LocaleLink } from "@i18n/routing";
import { cn } from "@repo/ui/lib/cn";
import { BannerShell } from "@shared/components/BannerShell";
import { BANNER_DISMISS_ICON_BUTTON_CLASS } from "@shared/lib/chrome/banner-dismiss-chrome";
import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactNode, useEffect, useState } from "react";

export const MARKETING_BANNER_SESSION_KEY = "sq-marketing-banner-dismissed-v1";

export interface MarketingBannerTitleIconProps {
	icon?: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
	className?: string;
}

export function MarketingBannerTitleIcon({
	icon: Icon = ClockIcon,
	className,
}: MarketingBannerTitleIconProps) {
	return (
		<Icon
			className={cn(
				"size-4 shrink-0 text-indigo-600 dark:text-indigo-200",
				className,
			)}
			aria-hidden
		/>
	);
}

export interface MarketingBannerLinkProps {
	href: string;
	title: ReactNode;
	message: ReactNode;
	cta: ReactNode;
	/** Decorative icon shown to the left of the title. */
	titleIcon?: ReactNode;
	/** Shown only at sm+; hidden on small screens with one fewer dot (title · cta). */
	className?: string;
	/**
	 * Full description for assistive tech when the middle segment is visually hidden on small screens.
	 * If omitted, the link has no explicit aria-label (visible text is title + CTA on mobile).
	 */
	accessibilityLabel?: string;
	linkClassName?: string;
}

function BannerDot({ className }: { className?: string }) {
	return (
		<svg
			viewBox="0 0 2 2"
			aria-hidden="true"
			className={cn("size-0.5 shrink-0 fill-current", className)}
		>
			<title />
			<circle r={1} cx={1} cy={1} />
		</svg>
	);
}

export interface MarketingBannerRowProps {
	title: ReactNode;
	message: ReactNode;
	cta: ReactNode;
	titleIcon?: ReactNode;
	className?: string;
}

export function MarketingBannerRow({
	title,
	message,
	cta,
	titleIcon,
	className,
}: MarketingBannerRowProps) {
	return (
		<span
			className={cn(
				"flex min-w-0 max-w-full flex-nowrap items-center justify-start gap-x-2 whitespace-nowrap min-[380px]:justify-center",
				className,
			)}
		>
			<strong className="inline-flex min-w-0 items-center gap-1.5 font-black max-[379px]:min-w-0 min-[380px]:shrink-0">
				<span className="shrink-0">{titleIcon}</span>
				<span className="min-w-0 max-[379px]:truncate">{title}</span>
			</strong>
			<BannerDot />
			<span className="hidden min-w-0 flex-1 basis-0 truncate sm:block">
				{message}
			</span>
			<BannerDot className="hidden sm:block" />
			<strong className="inline-flex shrink-0 items-center">{cta}</strong>
		</span>
	);
}

export function MarketingBannerLink({
	href,
	title,
	message,
	cta,
	titleIcon,
	className,
	accessibilityLabel,
	linkClassName,
}: MarketingBannerLinkProps) {
	return (
		<LocaleLink
			href={href}
			{...(accessibilityLabel != null && accessibilityLabel.length > 0
				? { "aria-label": accessibilityLabel }
				: {})}
			className={cn(
				"cursor-pointer block min-w-0 max-w-full hover:text-gray-600 dark:hover:text-white",
				linkClassName,
			)}
		>
			<MarketingBannerRow
				title={title}
				message={message}
				cta={cta}
				titleIcon={titleIcon}
				className={className}
			/>
		</LocaleLink>
	);
}

export interface MarketingBannerProps {
	titleIcon?: ReactNode;
}

export function MarketingBanner({
	titleIcon = <MarketingBannerTitleIcon />,
}: MarketingBannerProps = {}) {
	const t = useTranslations("marketing.banner");
	const [visible, setVisible] = useState(false);
	const [hydrated, setHydrated] = useState(false);

	useEffect(() => {
		setHydrated(true);
		try {
			if (sessionStorage.getItem(MARKETING_BANNER_SESSION_KEY) === "1") {
				setVisible(false);
				return;
			}
		} catch {
			/* sessionStorage unavailable */
		}
		setVisible(true);
	}, []);

	const dismiss = () => {
		try {
			sessionStorage.setItem(MARKETING_BANNER_SESSION_KEY, "1");
		} catch {
			/* ignore */
		}
		setVisible(false);
	};

	if (!hydrated || !visible) {
		return null;
	}

	const accessibilityLabel = `${t("kicker")}. ${t("messageLong")}. ${t("waitlistLink")}`;

	return (
		<BannerShell
			rightSlot={
				<button
					type="button"
					className={cn(BANNER_DISMISS_ICON_BUTTON_CLASS, "size-6")}
					onClick={dismiss}
					aria-label={t("dismissAria")}
				>
					<span className="sr-only">{t("dismissAria")}</span>
					<X
						className="size-3.5 shrink-0"
						strokeWidth={2.5}
						aria-hidden
					/>
				</button>
			}
		>
			<p className="min-w-0 text-gray-900 text-sm leading-6 dark:text-gray-100">
				<MarketingBannerLink
					href="/waitlist?earlyaccess=true"
					titleIcon={titleIcon}
					title={t("kicker")}
					message={t("messageLong")}
					cta={
						<>
							{t("waitlistLink")}
							&nbsp;<span aria-hidden="true">&rarr;</span>
						</>
					}
					accessibilityLabel={accessibilityLabel}
				/>
			</p>
		</BannerShell>
	);
}
